/**
 * CSSExporter.ts - Export themes and styles to static CSS files
 * 
 * CHANGE HISTORY:
 * - 251102_1840 danielbeer: Initial creation for Phase 3.6c
 * 
 * Exports Stylr themes and styles as production-ready CSS
 */

import type { Theme } from './ThemeInheritance';
import type { Style, StyleVariables } from '../types';
import { getThemeInheritanceManager } from './ThemeInheritance';
import { getStylr } from '../StylrManager';

export interface CSSExportOptions {
  /** Include CSS custom properties (variables) */
  includeVariables?: boolean;
  /** Include utility classes */
  includeUtilities?: boolean;
  /** Minify output */
  minify?: boolean;
  /** Add source comments */
  includeComments?: boolean;
  /** Prefix for CSS classes */
  classPrefix?: string;
  /** Target browsers for autoprefixer */
  browsers?: string[];
}

export interface ExportResult {
  css: string;
  size: number;
  variableCount: number;
  classCount: number;
}

export class CSSExporter {
  private options: Required<CSSExportOptions>;

  constructor(options: CSSExportOptions = {}) {
    this.options = {
      includeVariables: options.includeVariables ?? true,
      includeUtilities: options.includeUtilities ?? true,
      minify: options.minify ?? false,
      includeComments: options.includeComments ?? true,
      classPrefix: options.classPrefix ?? 'stylr-',
      browsers: options.browsers ?? ['> 1%', 'last 2 versions']
    };
  }

  /**
   * Export a theme to CSS
   */
  exportTheme(themeId: string): ExportResult {
    const themeMgr = getThemeInheritanceManager();
    const theme = themeMgr.getTheme(themeId);
    
    if (!theme) {
      throw new Error(`Theme not found: ${themeId}`);
    }

    const resolvedTheme = themeMgr.getResolvedTheme(themeId);
    if (!resolvedTheme) {
      throw new Error(`Failed to resolve theme: ${themeId}`);
    }

    let css = '';
    let variableCount = 0;
    let classCount = 0;

    // Add header comment
    if (this.options.includeComments) {
      css += this.generateHeader(theme);
    }

    // Add CSS variables
    if (this.options.includeVariables) {
      const varsCSS = this.generateVariables(resolvedTheme.variables);
      css += varsCSS;
      variableCount = Object.keys(resolvedTheme.variables).length;
    }

    // Add utility classes
    if (this.options.includeUtilities) {
      const utilsCSS = this.generateUtilityClasses(resolvedTheme.variables);
      css += utilsCSS;
      classCount = this.countClasses(utilsCSS);
    }

    // Minify if requested
    if (this.options.minify) {
      css = this.minifyCSS(css);
    }

    return {
      css,
      size: new Blob([css]).size,
      variableCount,
      classCount
    };
  }

  /**
   * Export a style to CSS
   */
  exportStyle(styleId: string): ExportResult {
    const stylr = getStylr();
    const style = stylr.getStyle(styleId);
    
    if (!style) {
      throw new Error(`Style not found: ${styleId}`);
    }

    let css = '';
    let variableCount = 0;
    let classCount = 0;

    // Add header comment
    if (this.options.includeComments) {
      css += `/**\n * Style: ${style.name}\n * Type: ${style.type}\n * Generated: ${new Date().toISOString()}\n */\n\n`;
    }

    // Generate CSS class
    const className = this.sanitizeClassName(style.name);
    const styleCSS = this.generateStyleClass(className, style.variables);
    css += styleCSS;
    classCount = 1;
    variableCount = Object.keys(style.variables).length;

    // Minify if requested
    if (this.options.minify) {
      css = this.minifyCSS(css);
    }

    return {
      css,
      size: new Blob([css]).size,
      variableCount,
      classCount
    };
  }

  /**
   * Export multiple themes to a single CSS file
   */
  exportThemes(themeIds: string[]): ExportResult {
    let combinedCSS = '';
    let totalVariables = 0;
    let totalClasses = 0;

    if (this.options.includeComments) {
      combinedCSS += `/**\n * Stylr Theme Bundle\n * Themes: ${themeIds.join(', ')}\n * Generated: ${new Date().toISOString()}\n */\n\n`;
    }

    themeIds.forEach(themeId => {
      const result = this.exportTheme(themeId);
      combinedCSS += result.css + '\n\n';
      totalVariables += result.variableCount;
      totalClasses += result.classCount;
    });

    if (this.options.minify) {
      combinedCSS = this.minifyCSS(combinedCSS);
    }

    return {
      css: combinedCSS,
      size: new Blob([combinedCSS]).size,
      variableCount: totalVariables,
      classCount: totalClasses
    };
  }

  /**
   * Export all themes from ThemeInheritanceManager
   */
  exportAllThemes(): ExportResult {
    const themeMgr = getThemeInheritanceManager();
    const themes = themeMgr.getAllThemes();
    const themeIds = themes.map(t => t.id);
    return this.exportThemes(themeIds);
  }

  /**
   * Generate header comment for theme
   */
  private generateHeader(theme: Theme): string {
    let header = '/**\n';
    header += ` * Theme: ${theme.name}\n`;
    if (theme.extends) {
      header += ` * Extends: ${theme.extends}\n`;
    }
    header += ` * Generated: ${new Date().toISOString()}\n`;
    header += ' */\n\n';
    return header;
  }

  /**
   * Generate CSS variables
   */
  private generateVariables(variables: Record<string, string | number>): string {
    let css = ':root {\n';
    
    Object.entries(variables).forEach(([key, value]) => {
      const indent = this.options.minify ? '' : '  ';
      const newline = this.options.minify ? '' : '\n';
      css += `${indent}${key}: ${value};${newline}`;
    });
    
    css += '}\n\n';
    return css;
  }

  /**
   * Generate utility classes from variables
   */
  private generateUtilityClasses(variables: Record<string, string | number>): string {
    let css = '';
    const prefix = this.options.classPrefix;

    // Background utilities
    if (variables['--bg']) {
      css += `.${prefix}bg { background: var(--bg); }\n`;
    }
    if (variables['--bg-surface']) {
      css += `.${prefix}bg-surface { background: var(--bg-surface); }\n`;
    }

    // Text utilities
    if (variables['--text']) {
      css += `.${prefix}text { color: var(--text); }\n`;
    }
    if (variables['--text-secondary']) {
      css += `.${prefix}text-secondary { color: var(--text-secondary); }\n`;
    }

    // Border utilities
    if (variables['--border']) {
      css += `.${prefix}border { border: var(--border); }\n`;
    }
    if (variables['--radius']) {
      css += `.${prefix}rounded { border-radius: var(--radius); }\n`;
    }

    // Padding utilities
    if (variables['--padding']) {
      css += `.${prefix}p { padding: var(--padding); }\n`;
    }

    // Shadow utilities
    if (variables['--box-shadow']) {
      css += `.${prefix}shadow { box-shadow: var(--box-shadow); }\n`;
    }

    return css + '\n';
  }

  /**
   * Generate CSS class for a style
   */
  private generateStyleClass(className: string, variables: Partial<StyleVariables>): string {
    const prefix = this.options.classPrefix;
    let css = `.${prefix}${className} {\n`;
    
    Object.entries(variables).forEach(([key, value]) => {
      const cssProperty = this.variableToCSSProperty(key);
      const indent = this.options.minify ? '' : '  ';
      const newline = this.options.minify ? '' : '\n';
      css += `${indent}${cssProperty}: ${value};${newline}`;
    });
    
    css += '}\n';
    return css;
  }

  /**
   * Convert CSS variable name to property name
   */
  private variableToCSSProperty(variable: string): string {
    // If it's already a CSS variable, use it directly
    if (variable.startsWith('--')) {
      return variable;
    }
    
    // Otherwise, convert camelCase to kebab-case
    return variable.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  /**
   * Sanitize class name
   */
  private sanitizeClassName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  /**
   * Count CSS classes in a string
   */
  private countClasses(css: string): number {
    const matches = css.match(/\.[a-zA-Z0-9_-]+\s*\{/g);
    return matches ? matches.length : 0;
  }

  /**
   * Minify CSS
   */
  private minifyCSS(css: string): string {
    return css
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
      .replace(/\s+/g, ' ') // Collapse whitespace
      .replace(/\s*([{}:;,])\s*/g, '$1') // Remove space around punctuation
      .replace(/;}/g, '}') // Remove last semicolon in block
      .trim();
  }

  /**
   * Download CSS as file
   */
  downloadCSS(css: string, filename: string): void {
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.css') ? filename : `${filename}.css`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Copy CSS to clipboard
   */
  async copyToClipboard(css: string): Promise<void> {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(css);
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = css;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }
}

// Singleton instance
let exporterInstance: CSSExporter | null = null;

export function getCSSExporter(options?: CSSExportOptions): CSSExporter {
  if (!exporterInstance || options) {
    exporterInstance = new CSSExporter(options);
  }
  return exporterInstance;
}

// Convenience functions
export function exportThemeToCSS(themeId: string, options?: CSSExportOptions): ExportResult {
  const exporter = getCSSExporter(options);
  return exporter.exportTheme(themeId);
}

export function exportStyleToCSS(styleId: string, options?: CSSExportOptions): ExportResult {
  const exporter = getCSSExporter(options);
  return exporter.exportStyle(styleId);
}

export function downloadThemeCSS(themeId: string, filename?: string, options?: CSSExportOptions): void {
  const exporter = getCSSExporter(options);
  const result = exporter.exportTheme(themeId);
  const themeMgr = getThemeInheritanceManager();
  const theme = themeMgr.getTheme(themeId);
  const defaultFilename = theme ? `${theme.name.toLowerCase().replace(/\s+/g, '-')}.css` : 'theme.css';
  exporter.downloadCSS(result.css, filename || defaultFilename);
}

export function downloadStyleCSS(styleId: string, filename?: string, options?: CSSExportOptions): void {
  const exporter = getCSSExporter(options);
  const result = exporter.exportStyle(styleId);
  const stylr = getStylr();
  const style = stylr.getStyle(styleId);
  const defaultFilename = style ? `${style.name.toLowerCase().replace(/\s+/g, '-')}.css` : 'style.css';
  exporter.downloadCSS(result.css, filename || defaultFilename);
}
